import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, CircularProgress, Box, Typography, Snackbar, Alert } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { createSpecialPrice } from '../../api/variants';
import { VariantInterface } from '../../types';

interface SpecialPriceDialogProps {
  open: boolean;
  variant: VariantInterface | null;
  onClose: () => void;
}

function SpecialPriceDialog({ open, variant, onClose }: SpecialPriceDialogProps): React.JSX.Element {
  const [loading, setLoading] = useState<boolean>(false);
  const [price, setPrice] = useState<string>('');
  const [specialPriceUrl, setSpecialPriceUrl] = useState<string>('');
  const [toastOpen, setToastOpen] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPrice(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!variant) return;
    
    setLoading(true);
    try {
      const token = (await createSpecialPrice(variant.id, {
        price: Number(price),
      })).token;
      const link = `${process.env.REACT_APP_CATALOG_URL}/variants?id=${variant.group_id}&price=${price}&variant_id=${variant.id}&token=${token}`;
      
      setSpecialPriceUrl(link);
    } catch (error) {
      console.error("Failed to create special price link", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (specialPriceUrl) {
      navigator.clipboard.writeText(specialPriceUrl);
      setToastOpen(true);
    }
  };

  const handleClose = (): void => {
    setPrice('');
    setSpecialPriceUrl('');
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>מחיר מיוחד</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              label="מחיר"
              type="number"
              value={price}
              onChange={handleChange}
              required
              fullWidth
              sx={{ mb: 2 }}
              disabled={loading || !!specialPriceUrl}
            />

            {specialPriceUrl && (
              <Box
                onClick={handleCopy}
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#e0e0e0'
                  }
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '85%'
                  }}
                >
                  {specialPriceUrl}
                </Typography>
                <ContentCopyIcon fontSize="small" color="action" />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>סגור</Button>
            {!specialPriceUrl && (
              <Button type='submit' variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'קביעת מחיר'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
      <Snackbar open={toastOpen} autoHideDuration={3000} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setToastOpen(false)} severity="success" sx={{ width: '100%' }}>
          הקישור הועתק בהצלחה!
        </Alert>
      </Snackbar>
    </>
  );
}

export default SpecialPriceDialog; 